'use client';

import { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import { FeedbackContext as FeedbackContextType, FeedbackState } from '@/types/feedback';

interface FeedbackContextValue {
  isOpen: boolean;
  context: FeedbackContextType | null;
  openFeedback: (context: FeedbackContextType) => void;
  closeFeedback: () => void;
}

const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined);

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FeedbackState>({
    isOpen: false,
    context: null,
  });

  const openFeedback = useCallback((context: FeedbackContextType) => {
    setState({ isOpen: true, context });
  }, []);

  const closeFeedback = useCallback(() => {
    setState({ isOpen: false, context: null });
  }, []);

  return (
    <FeedbackContext.Provider
      value={{
        isOpen: state.isOpen,
        context: state.context,
        openFeedback,
        closeFeedback,
      }}
    >
      {children}
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const context = useContext(FeedbackContext);
  if (context === undefined) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
}
